import React, { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { geoToIso } from './countryCodeMapping';

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const WorldMap = ({ data = {}, loading = false }) => {
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Log data for debugging
  useEffect(() => {
    console.log('Map data received (ISO codes):', data);
    console.log('Total countries:', Object.keys(data).length);
  }, [data]);

  // Calculate max value for color scale
  const maxValue = Math.max(...Object.values(data), 1);
  
  // Get color based on visitor count - Light purple theme like Plausible
  const getColor = (geoCountryCode) => {
    // Convert GeoJSON numeric code to ISO Alpha-2 (US, CA, etc.)
    const isoCode = geoToIso[geoCountryCode];
    
    // Try multiple variations
    let count = data[isoCode] || data[geoCountryCode] || 0;
    
    // Special case: UK vs GB
    if (count === 0 && isoCode === 'GB') {
      count = data['UK'] || 0;
    }
    
    if (count === 0) return '#f3f4f6'; // Light gray for no data
    
    // Purple gradient scale (light to bold)
    const intensity = count / maxValue;
    if (intensity > 0.8) return '#5b21b6';  // Bold purple - highest
    if (intensity > 0.6) return '#7c3aed';  // Dark purple
    if (intensity > 0.4) return '#8b5cf6';  // Medium purple
    if (intensity > 0.2) return '#a78bfa';  // Light purple
    return '#c4b5fd'; // Very light purple - lowest
  };

  const handleMouseEnter = (geo) => {
    const geoCountryCode = geo.id;
    const countryName = geo.properties.name;
    
    // Convert GeoJSON numeric code to ISO Alpha-2
    const isoCode = geoToIso[geoCountryCode];
    
    // Try multiple variations to match data
    // Some databases use "UK" instead of "GB" for United Kingdom
    let count = data[isoCode] || data[geoCountryCode] || 0;
    
    // Special case: UK vs GB
    if (count === 0 && isoCode === 'GB') {
      count = data['UK'] || 0;
    }
    
    // Debug logging
    console.log(`Hovered: ${countryName} (Geo: ${geoCountryCode}, ISO: ${isoCode}), Count: ${count}`);
    
    // Always show tooltip, even for countries with 0 visitors
    setHoveredCountry({
      name: countryName,
      count: count,
      code: isoCode || geoCountryCode
    });
  };
  
  const handleMouseMove = (event) => {
    // Update mouse position for tooltip positioning
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredCountry(null);
  };
  
  // Format visitor count (e.g., 1600 -> "1.6k")
  const formatCount = (count) => {
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1) + 'M';
    } else if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'k';
    }
    return count.toString();
  };

  // Loading skeleton
  if (loading) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '450px', backgroundColor: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="animate-pulse" style={{ textAlign: 'center' }}>
          <div style={{ width: '100%', height: '300px', backgroundColor: '#e5e7eb', borderRadius: '8px', marginBottom: '16px' }}></div>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>Loading map data...</div>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{ position: 'relative', width: '100%', height: '450px' }}
      onMouseMove={handleMouseMove}
    >
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 130,
        }}
        style={{ width: '100%', height: '100%' }}
      >
        <ZoomableGroup center={[0, 20]} zoom={1}>
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const countryCode = geo.id;
                const count = data[countryCode] || 0;
                const isHovered = hoveredCountry?.code === countryCode;
                
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={getColor(countryCode)}
                    stroke={isHovered ? '#4338ca' : '#fff'}
                    strokeWidth={isHovered ? 2 : 2}
                    style={{
                      default: { 
                        outline: 'none',
                        transition: 'all 0.2s ease-in-out'
                      },
                      hover: {
                        fill: '#4338ca',
                        outline: 'none',
                        cursor: 'pointer',
                        stroke: '#4338ca',
                        strokeWidth: 2,
                        transition: 'all 0.2s ease-in-out'
                      },
                      pressed: { outline: 'none' },
                    }}
                    onMouseEnter={() => handleMouseEnter(geo)}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Cursor-Following Tooltip */}
      {hoveredCountry && (
        <div
          style={{
            position: 'fixed',
            left: `${mousePosition.x + 15}px`,
            top: `${mousePosition.y - 50}px`,
            background: 'rgba(255, 255, 255, 0.98)',
            color: '#1f2937',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '400',
            pointerEvents: 'none',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            whiteSpace: 'nowrap',
            transition: 'all 0.1s ease-out'
          }}
        >
          <div style={{ 
            fontSize: '15px', 
            fontWeight: '600', 
            marginBottom: '4px',
            color: '#111827'
          }}>
            {hoveredCountry.name}
          </div>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '500',
            color: '#5046e4'
          }}>
            {formatCount(hoveredCountry.count)} Visitors
          </div>
        </div>
      )}

      {/* Color Legend */}
      <div
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          background: 'white',
          padding: '12px',
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#6b7280' }}>
          Visitors
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '60px', height: '8px', background: 'linear-gradient(to right, #e5e0ff, #5046e4)', borderRadius: '4px' }}></div>
          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
            <span>0</span>
            <span style={{ float: 'right' }}>{maxValue.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorldMap;
